/**
 * MEGA Settings Panel - Complete Restaurant Configuration
 * Professional settings system better than any competitor
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, Receipt, Euro, Users, Image as ImageIcon, Clock, 
  Percent, Upload, Save, Settings, CheckCircle, AlertCircle, Truck, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import ProductImageSettings from '../components/ProductImageSettings';
import DeliveryPlatformSettings from '../components/DeliveryPlatformSettings';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function MegaSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');

  // Form states
  const [companyForm, setCompanyForm] = useState({});
  const [receiptForm, setReceiptForm] = useState({});
  const [openingHours, setOpeningHours] = useState({});
  const [discounts, setDiscounts] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/settings-advanced/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setCompanyForm(data.company || {});
        setReceiptForm(data.receipt_config || {});
        setOpeningHours(data.opening_hours || {});
        setDiscounts(data.discounts || []);
        if (data.company?.logo_url) {
          setLogoPreview(data.company.logo_url);
        }
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;

    const formData = new FormData();
    formData.append('file', logoFile);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/settings-advanced/logo/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setLogoPreview(data.logo_url);
        toast.success('Logo hochgeladen!');
        loadAllSettings();
      }
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    }
  };

  const saveReceiptConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/settings-advanced/receipt-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(receiptForm)
      });

      if (response.ok) {
        toast.success('Bon-Einstellungen gespeichert!');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const saveOpeningHours = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/settings-advanced/opening-hours`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(openingHours)
      });

      if (response.ok) {
        toast.success('Öffnungszeiten gespeichert!');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628]">
        <div className="text-white">Lade Einstellungen...</div>
      </div>
    );
  }

  const weekdays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628] p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Einstellungen
          </h1>
          <p className="text-slate-400">
            Komplette Konfiguration Ihres Kassensystems
          </p>
        </div>

        {/* Main Settings Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#111C32] border border-slate-700/50 p-1 flex-wrap h-auto">
            <TabsTrigger value="company" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />
              Unternehmen
            </TabsTrigger>
            <TabsTrigger value="receipt" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <Receipt className="w-4 h-4 mr-2" />
              Bon-Layout
            </TabsTrigger>
            <TabsTrigger value="hours" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <Clock className="w-4 h-4 mr-2" />
              Öffnungszeiten
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <Euro className="w-4 h-4 mr-2" />
              Preise & Rabatte
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <ImageIcon className="w-4 h-4 mr-2" />
              Produkte
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Benutzer
            </TabsTrigger>
            <TabsTrigger value="delivery" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <Truck className="w-4 h-4 mr-2" />
              Lieferportale
            </TabsTrigger>
            <TabsTrigger value="order-rules" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Bestellregeln
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: UNTERNEHMEN */}
          <TabsContent value="company">
            <Card className="bg-[#111C32] border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Unternehmensdaten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Logo Upload */}
                <div>
                  <Label className="text-slate-300 mb-3 block">Restaurant-Logo</Label>
                  <div className="flex gap-4 items-start">
                    {logoPreview && (
                      <div className="w-24 h-24 rounded-lg border-2 border-slate-700 bg-slate-800 flex items-center justify-center overflow-hidden">
                        <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setLogoFile(file);
                            setLogoPreview(URL.createObjectURL(file));
                          }
                        }}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload">
                        <Button asChild variant="outline" className="cursor-pointer border-slate-600 text-white hover:bg-slate-700">
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            Logo auswählen
                          </span>
                        </Button>
                      </label>
                      {logoFile && (
                        <Button
                          onClick={uploadLogo}
                          className="ml-2 bg-teal-500 hover:bg-teal-600"
                        >
                          Hochladen
                        </Button>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        Empfohlen: 360x360px, max 2MB
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-300">Firmenname</Label>
                    <Input
                      value={companyForm.name || ''}
                      onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                      className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                      placeholder="Zozo Burger GmbH"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label className="text-slate-300">Adresse</Label>
                    <Input
                      value={companyForm.address?.street || ''}
                      className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                      placeholder="Hauptstraße 42, 10115 Berlin"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Telefon</Label>
                    <Input
                      value={companyForm.phone || ''}
                      className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                      placeholder="+49 30 12345678"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">USt-IdNr</Label>
                    <Input
                      value={companyForm.vat_id || ''}
                      className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                      placeholder="DE123456789"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 2: ÖFFNUNGSZEITEN */}
          <TabsContent value="hours">
            <Card className="bg-[#111C32] border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Öffnungszeiten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {weekdays.map((day, index) => {
                  const dayKey = day.toLowerCase();
                  const dayData = openingHours[dayKey] || { open: '11:00', close: '22:00', closed: false };
                  
                  return (
                    <div key={day} className="flex items-center gap-4 p-4 bg-slate-800/30 rounded-lg">
                      <div className="w-24">
                        <span className="text-white font-medium">{day}</span>
                      </div>
                      
                      <Switch
                        checked={!dayData.closed}
                        onCheckedChange={(checked) => {
                          setOpeningHours({
                            ...openingHours,
                            [dayKey]: { ...dayData, closed: !checked }
                          });
                        }}
                        className="data-[state=checked]:bg-teal-500"
                      />
                      
                      {!dayData.closed && (
                        <>
                          <Input
                            type="time"
                            value={dayData.open || '11:00'}
                            onChange={(e) => {
                              setOpeningHours({
                                ...openingHours,
                                [dayKey]: { ...dayData, open: e.target.value }
                              });
                            }}
                            className="w-32 bg-[#0E1628] border-slate-700 text-white"
                          />
                          <span className="text-slate-400">bis</span>
                          <Input
                            type="time"
                            value={dayData.close || '22:00'}
                            onChange={(e) => {
                              setOpeningHours({
                                ...openingHours,
                                [dayKey]: { ...dayData, close: e.target.value }
                              });
                            }}
                            className="w-32 bg-[#0E1628] border-slate-700 text-white"
                          />
                        </>
                      )}
                      
                      {dayData.closed && (
                        <Badge variant="outline" className="text-red-400 border-red-400/50">
                          Geschlossen
                        </Badge>
                      )}
                    </div>
                  );
                })}
                
                <Button
                  onClick={saveOpeningHours}
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white h-12"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Öffnungszeiten speichern
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 3: BON-LAYOUT */}
          <TabsContent value="receipt">
            <Card className="bg-[#111C32] border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Bon-Einstellungen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                <div>
                  <Label className="text-slate-300">Kopfzeile</Label>
                  <Textarea
                    value={receiptForm.header_text || ''}
                    onChange={(e) => setReceiptForm({...receiptForm, header_text: e.target.value})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white min-h-[100px]"
                    placeholder="Zozo Burger GmbH&#10;Hauptstraße 42&#10;10115 Berlin"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Fußzeile</Label>
                  <Textarea
                    value={receiptForm.footer_text || ''}
                    onChange={(e) => setReceiptForm({...receiptForm, footer_text: e.target.value})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    placeholder="Vielen Dank für Ihren Besuch!"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                    <Label className="text-slate-300">Logo anzeigen</Label>
                    <Switch
                      checked={receiptForm.show_logo !== false}
                      onCheckedChange={(checked) => setReceiptForm({...receiptForm, show_logo: checked})}
                      className="data-[state=checked]:bg-teal-500"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                    <Label className="text-slate-300">MwSt-Details</Label>
                    <Switch
                      checked={receiptForm.show_vat_details !== false}
                      onCheckedChange={(checked) => setReceiptForm({...receiptForm, show_vat_details: checked})}
                      className="data-[state=checked]:bg-teal-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-slate-300 mb-2 block">Schriftgröße</Label>
                    {['normal', 'large'].map((size) => (
                      <button
                        key={size}
                        onClick={() => setReceiptForm({...receiptForm, font_size: size})}
                        className={`w-full p-3 rounded-lg border-2 mb-2 ${
                          receiptForm.font_size === size
                            ? 'border-teal-500 bg-teal-500/15'
                            : 'border-slate-700 bg-slate-800/30'
                        }`}
                      >
                        <span className="text-white capitalize">{size}</span>
                      </button>
                    ))}
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 mb-2 block">Papierbreite</Label>
                    {['58mm', '80mm'].map((width) => (
                      <button
                        key={width}
                        onClick={() => setReceiptForm({...receiptForm, paper_width: width})}
                        className={`w-full p-3 rounded-lg border-2 mb-2 ${
                          receiptForm.paper_width === width
                            ? 'border-teal-500 bg-teal-500/15'
                            : 'border-slate-700 bg-slate-800/30'
                        }`}
                      >
                        <span className="text-white">{width}</span>
                      </button>
                    ))}
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 mb-2 block">Bon-Kopien</Label>
                    {[1, 2, 3].map((num) => (
                      <button
                        key={num}
                        onClick={() => setReceiptForm({...receiptForm, copies: num})}
                        className={`w-full p-3 rounded-lg border-2 mb-2 ${
                          receiptForm.copies === num
                            ? 'border-teal-500 bg-teal-500/15'
                            : 'border-slate-700 bg-slate-800/30'
                        }`}
                      >
                        <span className="text-white">{num}x</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={saveReceiptConfig}
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white h-12"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Bon-Einstellungen speichern
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: PREISE & RABATTE */}
          <TabsContent value="pricing">
            <Card className="bg-[#111C32] border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Preise & Rabatte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Rabatte Liste */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-slate-300 text-lg">Aktive Rabatte</Label>
                    <Button
                      size="sm"
                      className="bg-teal-500 hover:bg-teal-600"
                      onClick={() => {
                        // Add new discount
                        const newDiscount = {
                          name: 'Neuer Rabatt',
                          type: 'percentage',
                          value: 10,
                          active: false
                        };
                        toast.info('Rabatt-Editor (vereinfacht)', { description: 'Backend ready - UI kommt in v2' });
                      }}
                    >
                      + Rabatt hinzufügen
                    </Button>
                  </div>
                  
                  {discounts.length === 0 ? (
                    <div className="p-8 bg-slate-800/30 rounded-lg border border-slate-700/50 text-center">
                      <Percent className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                      <p className="text-slate-400 text-sm">Noch keine Rabatte konfiguriert</p>
                      <p className="text-slate-500 text-xs mt-1">z.B. Happy Hour, Lunch-Menü, Aktionen</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {discounts.map((discount) => (
                        <div key={discount.discount_id} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-white font-semibold">{discount.name}</div>
                              <div className="text-sm text-slate-400 mt-1">
                                {discount.type === 'percentage' ? `${discount.value}%` : `${discount.value}€`} Rabatt
                              </div>
                            </div>
                            <Switch
                              checked={discount.active}
                              className="data-[state=checked]:bg-emerald-500"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Preislisten Info */}
                <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <div className="flex gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <div className="text-sm text-slate-300">
                      <strong className="text-blue-400">Preislisten:</strong> Lunch/Dinner Preise, Happy Hour, etc. 
                      Backend ready - UI kommt in v2
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 5: BENUTZER & PIN */}
          <TabsContent value="users">
            <Card className="bg-[#111C32] border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Benutzer & Zugriff
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* PIN-Login Info */}
                <div className="p-6 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-xl border border-emerald-500/30">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">PIN-Code Login (Schnellzugriff)</h3>
                      <p className="text-sm text-slate-300 mb-3">
                        Kassierer können sich mit 4-6 stelligem PIN anmelden statt E-Mail/Passwort
                      </p>
                      <Badge variant="outline" className="text-emerald-400 border-emerald-400/50 bg-emerald-400/10">
                        ✅ Backend implementiert
                      </Badge>
                      <Badge variant="outline" className="ml-2 text-amber-400 border-amber-400/50 bg-amber-400/10">
                        UI kommt in v2
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Berechtigungen Info */}
                <div className="p-6 bg-slate-800/30 rounded-lg border border-slate-700/50">
                  <h3 className="text-white font-semibold mb-3">Berechtigungen & Rollen</h3>
                  <div className="space-y-2 text-sm text-slate-300">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>Admin: Vollzugriff auf alle Funktionen</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      <span>Kassierer: Bestellungen, Zahlung, keine Einstellungen</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-amber-400" />
                      <span>Kellner: Nur Bestellungen, kein Kassenzugriff</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Fein-granulare Berechtigungen kommen in v2
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 6: PRODUKTBILDER */}
          <TabsContent value="products">
            <ProductImageSettings />
          </TabsContent>

          {/* TAB 7: LIEFERPORTALE */}
          <TabsContent value="delivery">
            <DeliveryPlatformSettings />
          </TabsContent>

          {/* TAB 8: BESTELLUNGSREGELN */}
          <TabsContent value="order-rules">
            <div className="space-y-4">
              <Card className="bg-[#111C32] border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    <ShoppingCart className="w-5 h-5 text-teal-400" />
                    Bestellungsregeln & Verfügbarkeit
                  </CardTitle>
                  <p className="text-slate-400 text-sm">
                    Vollständige Kontrolle: Mindestbestellwert, Liefergebiete, Zeitregeln, Kapazitätslimits
                  </p>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => window.location.href = '/app/bestellungsregeln'}
                    className="w-full h-14 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white text-lg font-semibold"
                    data-testid="open-order-rules-btn"
                  >
                    Bestellungsregeln öffnen
                  </Button>
                  
                  <div className="mt-6 grid grid-cols-3 gap-3">
                    <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                      <CheckCircle className="w-5 h-5 text-emerald-400 mb-2" />
                      <div className="text-xs text-slate-400 mb-1">Mindestbestellwert</div>
                      <div className="text-white font-semibold">Pro Bestelltyp</div>
                    </div>
                    <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                      <CheckCircle className="w-5 h-5 text-emerald-400 mb-2" />
                      <div className="text-xs text-slate-400 mb-1">Liefergebiete</div>
                      <div className="text-white font-semibold">PLZ-basiert</div>
                    </div>
                    <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                      <CheckCircle className="w-5 h-5 text-emerald-400 mb-2" />
                      <div className="text-xs text-slate-400 mb-1">Produktzeiten</div>
                      <div className="text-white font-semibold">Automatisch</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}
